/* WLM UK "Find your photos" search.
 * Used on the Find your photos page (all events) and on event product pages
 * (filtered to that product when an event is linked to it). Data comes from the
 * photo_finder_event metaobjects (one per Pixieset collection, with its lifter
 * sets), rendered as JSON by templates/search.photo-index.liquid.
 */
(function () {
  'use strict';

  var MAX_SHOWN = 40;
  var CACHE_KEY = 'wlm-pf-index-v5';
  var RECENT_STEP = 10;
  var CACHE_MINUTES = 30;

  // Lower case, accents removed, punctuation to spaces: "Zoë O'Neill" -> "zoe o neill"
  function norm(s) {
    return String(s || '')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // One search entry per lifter set: { n name, e event, d date, u set link, t cover, p product }.
  function flatten(events) {
    var out = [];
    events.forEach(function (ev) {
      (ev.l || []).forEach(function (l) {
        // A lifter is "Name" (set slug is the name squashed: "Harry Smith" -> harrysmith),
        // or [name, slug] / [name, full link] when Pixieset's slug differs.
        if (typeof l === 'string') l = [l, norm(l).replace(/ /g, '')];
        var link = !l[1] ? ev.u : /^https?:/.test(l[1]) ? l[1] : ev.u.replace(/\/?$/, '/') + l[1] + '/';
        out.push({ n: l[0], u: link, e: ev.t, d: ev.d, t: ev.img, p: ev.p });
      });
    });
    return out;
  }

  function prepare(entries) {
    return entries.filter(function (e) { return e.n && e.u; }).map(function (e) {
      var words = norm(e.n).split(' ');
      return Object.assign({}, e, { _words: words, _full: words.join(' ') });
    }).sort(function (a, b) {
      return (b.d || '').localeCompare(a.d || '') || a.n.localeCompare(b.n);
    });
  }

  // Every typed word must start one of the lifter's name words, in any order,
  // so "harry", "harry sm" and "smith harry" all find Harry Smith.
  function search(entries, query) {
    var terms = norm(query).split(' ').filter(Boolean);
    if (!terms.length) return [];
    return entries.filter(function (e) {
      return terms.every(function (t) {
        // Also joins following words, so "oneill" finds O'Neill.
        return e._words.some(function (w, i) {
          return w.indexOf(t) === 0 || e._words.slice(i).join('').indexOf(t) === 0;
        });
      });
    });
  }

  function highlight(name, query) {
    var terms = norm(query).split(' ').filter(Boolean);
    return name.split(/(\s+)/).map(function (part) {
      var n = norm(part);
      var hit = terms.filter(function (t) { return n.indexOf(t) === 0; })
        .sort(function (a, b) { return b.length - a.length; })[0];
      if (!hit) return esc(part);
      // Map the matched length back onto the original characters.
      var i = 0, seen = 0;
      while (i < part.length && seen < hit.length) {
        if (norm(part[i])) seen++;
        i++;
      }
      return '<mark>' + esc(part.slice(0, i)) + '</mark>' + esc(part.slice(i));
    }).join('');
  }

  function readCache() {
    try {
      var c = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (c && Date.now() - c.at < CACHE_MINUTES * 60000) return c.data;
    } catch (e) {}
    return null;
  }

  function writeCache(data) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: data })); } catch (e) {}
  }

  function fetchPage(feed, page) {
    var sep = feed.indexOf('?') === -1 ? '?' : '&';
    return fetch(feed + sep + 'page=' + page, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (t) { return JSON.parse(t); });
  }

  // Page 1 says how many pages there are; the rest load in parallel.
  // A page that fails is retried once, then skipped, so one bad page never
  // blanks the whole search. Resolves to the list of events (Pixieset collections).
  function loadFeed(feed) {
    var cached = readCache();
    if (cached) return Promise.resolve(cached);
    function tryPage(p) {
      return fetchPage(feed, p).catch(function () { return fetchPage(feed, p); });
    }
    return tryPage(1).then(function (first) {
      var rest = [], failed = false;
      for (var p = 2; p <= first.pages; p++) {
        rest.push(tryPage(p).catch(function () { failed = true; return { events: [] }; }));
      }
      return Promise.all(rest).then(function (pages) {
        // Skip any page the store answers with an earlier page's data.
        var events = [], seen = {};
        [first].concat(pages).forEach(function (pg) {
          if (seen[pg.page]) return;
          seen[pg.page] = true;
          events = events.concat(pg.events || []);
        });
        if (!failed) writeCache(events);
        return events;
      });
    });
  }

  function byNewest(events) {
    return events.filter(function (ev) { return ev.u; }).slice().sort(function (a, b) {
      return (b.d || '').localeCompare(a.d || '') || String(a.t).localeCompare(String(b.t));
    });
  }

  var TICK = '<svg class="wlm-pf__tick" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="12"/><path d="m6.5 12.5 3.6 3.6 7.4-8" fill="none" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // An event row opens its Pixieset collection.
  function recentRow(ev) {
    var img = ev.img
      ? '<img class="wlm-pf__event-img" src="' + esc(ev.img) + '" alt="" loading="lazy" width="64" height="64">'
      : '<span class="wlm-pf__event-img" aria-hidden="true"></span>';
    var n = (ev.l || []).length;
    var when = [formatDate(ev.d), n ? n + (n === 1 ? ' lifter' : ' lifters') : ''].filter(Boolean).join(' · ');
    return '<li><a class="wlm-pf__event-row" href="' + esc(ev.u) + '" target="_blank" rel="noopener">' + img +
      '<span class="wlm-pf__text"><span class="wlm-pf__name">' + esc(ev.t) + '</span>' +
      (when ? '<span class="wlm-pf__when">' + esc(when) + '</span>' : '') + '</span>' +
      TICK + '</a></li>';
  }

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso + 'T12:00:00');
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  function card(e, query, showBuy) {
    var thumb = e.t
      ? '<img class="wlm-pf__thumb" src="' + esc(e.t) + '" alt="" loading="lazy" width="96" height="120">'
      : '<span class="wlm-pf__thumb wlm-pf__thumb--empty" aria-hidden="true"></span>';
    var when = [e.day, formatDate(e.d)].filter(Boolean).join(' · ');
    var buy = showBuy && e.p
      ? '<a class="wlm-pf__buy" href="/products/' + esc(e.p) + '">Buy your photo pack</a>'
      : '';
    return '<li class="wlm-pf__item">' +
      '<a class="wlm-pf__card" href="' + esc(e.u) + '" target="_blank" rel="noopener">' +
        thumb +
        '<span class="wlm-pf__text">' +
          '<span class="wlm-pf__name">' + highlight(e.n, query) + '</span>' +
          '<span class="wlm-pf__event">' + esc(e.e) + '</span>' +
          (when ? '<span class="wlm-pf__when">' + esc(when) + '</span>' : '') +
        '</span>' +
        '<span class="wlm-pf__go">View photos<span aria-hidden="true"> →</span></span>' +
      '</a>' + buy +
    '</li>';
  }

  function mount(root, opts) {
    opts = opts || {};
    var input = root.querySelector('.wlm-pf__input');
    var status = root.querySelector('.wlm-pf__status');
    var list = root.querySelector('.wlm-pf__results');
    var product = root.getAttribute('data-product') || opts.product || '';
    var minChars = product ? 1 : 2;
    var entries = null, recent = [], recentShown = RECENT_STEP, loading = null, timer = null, more = null;
    // Main page: recent events + help, shown when empty or nothing matches.
    // Product page: just the help line, shown when nothing matches.
    var recentBox = document.createElement('div');
    recentBox.className = 'wlm-pf__recent';
    recentBox.hidden = true;
    root.appendChild(recentBox);

    var pixieset = root.getAttribute('data-pixieset') || '';
    var contact = root.getAttribute('data-contact') || '/pages/contact';

    function help() {
      var preview = pixieset
        ? '<a class="wlm-pf__help-btn" href="' + esc(pixieset) + '" target="_blank" rel="noopener">Can\'t find yourself? Double check our Preview website</a>'
        : 'Can\'t find yourself?';
      return '<p class="wlm-pf__help">' + preview + ' <span>or <a href="' + esc(contact) + '">contact us</a>.</span></p>';
    }

    function showRecent(show, noMatch) {
      recentBox.hidden = !show || (product && !noMatch);
      if (recentBox.hidden) return;
      if (product) { recentBox.innerHTML = help(); return; }
      var title = !recent.length ? 'No events are up for preview here yet.'
        : noMatch ? 'Is your event here? These are the latest events with previews up.'
        : 'Latest events ready to preview';
      recentBox.innerHTML =
        '<p class="wlm-pf__recent-note">Previews usually go up within a few days of the competition. If your event isn\'t listed yet, check back soon!</p>' +
        help() +
        '<h3 class="wlm-pf__recent-title">' + title + '</h3>' +
        (recent.length ? '<ul class="wlm-pf__recent-list">' + recent.slice(0, recentShown).map(recentRow).join('') + '</ul>' : '') +
        (recent.length > recentShown ? '<button type="button" class="wlm-pf__more">Show more events</button>' : '');
      watchMore();
    }

    // Ten events at a time; more appear as the visitor scrolls to the end
    // (or taps "Show more events" where scrolling can't be watched).
    function showMoreEvents() {
      if (recentShown >= recent.length) return;
      var list = recentBox.querySelector('.wlm-pf__recent-list');
      var next = recent.slice(recentShown, recentShown + RECENT_STEP);
      recentShown += next.length;
      list.insertAdjacentHTML('beforeend', next.map(recentRow).join(''));
      if (recentShown >= recent.length) {
        var btn = recentBox.querySelector('.wlm-pf__more');
        if (btn) btn.remove();
        if (more) { more.disconnect(); more = null; }
      }
    }

    function watchMore() {
      var btn = recentBox.querySelector('.wlm-pf__more');
      if (more) { more.disconnect(); more = null; }
      if (!btn) return;
      btn.addEventListener('click', showMoreEvents);
      if ('IntersectionObserver' in window) {
        more = new IntersectionObserver(function (seen) {
          if (seen[0].isIntersecting) showMoreEvents();
        }, { rootMargin: '300px' });
        more.observe(btn);
      }
    }

    function ensureLoaded() {
      if (entries) return Promise.resolve(entries);
      if (!loading) {
        var src = opts.events ? Promise.resolve(opts.events) : loadFeed(root.getAttribute('data-feed'));
        loading = src.then(function (events) {
          // On a product page, search only that event's collections when any are linked to it.
          if (product) {
            var mine = events.filter(function (ev) { return ev.p === product; });
            if (mine.length) events = mine;
          }
          recent = byNewest(events);
          entries = prepare(flatten(recent));
          return entries;
        });
        loading.catch(function () { loading = null; });
      }
      return loading;
    }

    function render() {
      var q = input.value;
      if (norm(q).length < minChars) {
        list.innerHTML = '';
        status.textContent = '';
        ensureLoaded().then(function () {
          if (norm(input.value).length < minChars) showRecent(true);
        }, function () {
          showRecent(true);
        });
        return;
      }
      ensureLoaded().then(function (all) {
        if (input.value !== q) return;
        var hits = search(all, q);
        list.innerHTML = hits.slice(0, MAX_SHOWN).map(function (e) { return card(e, q, !product); }).join('');
        showRecent(!hits.length, true);
        if (!hits.length) {
          status.textContent = product
            ? 'No lifter called "' + q.trim() + '" at this event yet. Galleries go up within a few days of the competition.'
            : 'No lifter called "' + q.trim() + '" yet. Check the spelling, or try just your surname.';
        } else if (hits.length > MAX_SHOWN) {
          status.textContent = hits.length + ' matches. Keep typing your surname to narrow them down.';
        } else {
          status.textContent = hits.length === 1 ? '1 gallery found' : hits.length + ' galleries found';
        }
      }).catch(function () {
        status.textContent = 'The photo search could not load. Refresh the page to try again.';
        showRecent(true, true);
      });
    }

    input.addEventListener('focus', ensureLoaded, { once: true });
    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(render, 80);
    });
    root.addEventListener('submit', function (ev) { ev.preventDefault(); render(); });
    if (input.value || !product) render();
  }

  window.WLMPhotoFinder = { mount: mount, _search: search, _norm: norm };

  function autoMount() {
    document.querySelectorAll('.wlm-pf[data-feed]:not([data-mounted])').forEach(function (el) {
      el.setAttribute('data-mounted', '');
      mount(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoMount);
  else autoMount();
  document.addEventListener('shopify:section:load', autoMount);
})();
